import { motion } from "framer-motion";
import { Wallet, Home, ShieldCheck, Briefcase, Plane, Car, Cog, Bot, Plane as PlaneIcon, Factory, Building2, Wrench } from "lucide-react";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

const FEATURE_ICONS = [Wallet, Home, ShieldCheck, Briefcase, Plane];
const CAREER_ICONS = [Car, Cog, Bot, PlaneIcon, Factory, Building2, Wrench, Cog];

export function WhyKiki() {
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const features = t("why.features");
  const careers = t("why.careers");

  return (
    <section id="manifesto" data-testid={KIKI.manifesto} className="relative py-16 md:py-24">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Left: features */}
          <div className="col-span-12 lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-6"
            >
              <span className="font-mono text-sm text-muted-foreground">01</span>
              <span className={`label-overline text-muted-foreground ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("why.overline1")}</span>
              <span className={`text-lg md:text-2xl font-bold w-full sm:w-auto ${isHi ? "font-hindi" : "font-sans"}`}>{t("why.title1")}</span>
            </motion.div>

            <div className="border border-border rounded-2xl bg-card overflow-hidden">
              {features.map((f, i) => {
                const Icon = FEATURE_ICONS[i] || Wallet;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-start gap-4 p-5 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors"
                  >
                    <div className="w-10 h-10 shrink-0 grid place-items-center rounded-lg bg-[#FFCC00] text-black">
                      <Icon className="w-4 h-4" strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className={`font-semibold text-[15px] leading-tight ${isHi ? "font-hindi" : ""}`}>{f.title}</div>
                      <div className={`text-sm text-muted-foreground mt-1 ${isHi ? "font-hindi" : ""}`}>{f.body}</div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Right: careers */}
          <div className="col-span-12 lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-6"
            >
              <span className="font-mono text-sm text-muted-foreground">02</span>
              <span className={`label-overline text-muted-foreground ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("why.overline2")}</span>
              <span className={`text-lg md:text-2xl font-bold w-full sm:w-auto ${isHi ? "font-hindi" : "font-sans"}`}>{t("why.title2")}</span>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#0A0A0A] rounded-2xl p-4 md:p-5">
              {careers.map((label, i) => {
                const Icon = CAREER_ICONS[i] || Cog;
                return (
                  <motion.div
                    key={label + i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-3 p-3 rounded-xl border border-white/10 hover:border-[#FFCC00]/40 hover:bg-white/5 transition-colors"
                  >
                    <div className="w-9 h-9 grid place-items-center rounded-lg bg-[#FFCC00]/15 text-[#FFCC00]">
                      <Icon className="w-4 h-4" strokeWidth={1.8} />
                    </div>
                    <span className={`text-white/90 text-sm font-medium ${isHi ? "font-hindi" : ""}`}>{label}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
